import { forwardRef } from 'react';
import { Box, SxProps } from '@mui/material';

import { colors } from '@constants/colors';

import { usePulsingBorder } from './hooks';
import { useStyles } from './styles';
import { IPulsingBorder } from './interfaces';

/**
 * Wraps any child content with a ring that pulses on and off in a repeating interval.
 * Purely a visual wrapper — it does not alter the size, layout, or behavior of its children.
 * Forwards its ref and spreads unknown props onto the wrapper so it also works as the
 * direct child of components that clone their child (e.g. MUI `Tooltip`).
 *
 * @param props.children - Content to render inside the pulsing border.
 * @param props.borderColor - CSS color for the ring and its glow; defaults to white.
 * @param props.borderWidth - Ring thickness in pixels.
 * @param props.borderRadius - Border radius passed through to the wrapper.
 * @param props.pulseDuration - Roughly how long, in seconds, the border pulses before pausing.
 * Snapped up to the nearest whole number of `speed`-long breaths so the last one always
 * finishes (and eases back to rest) instead of being cut off mid-glow.
 * @param props.pauseDuration - How long, in seconds, the border stays still before pulsing again.
 * @param props.speed - Duration, in seconds, of a single glow breath while pulsing.
 * @param props.fadeDuration - Seconds any residual box-shadow change (e.g. a color swap) eases over.
 * @param props.active - Turns the ring on/off; false (default) renders the child with no ring at all.
 * @param props.sx - Additional MUI `sx` overrides merged onto the wrapper.
 * @returns The wrapped children with a `div` ref forwarded to the outer wrapper element.
 */
const PulsingBorder = forwardRef<HTMLDivElement, IPulsingBorder>(
  (
    {
      children,
      borderColor = colors.white,
      borderWidth = 2,
      borderRadius = 0,
      pulseDuration = 1.5,
      pauseDuration = 1.5,
      speed = 1,
      fadeDuration = 0.6,
      active = false,
      sx = {},
      ...rest
    },
    ref,
  ) => {
    const breathMs = speed * 1000;
    const iterationCount = Math.max(
      1,
      Math.round((pulseDuration * 1000) / breathMs),
    );
    const effectivePulseDurationMs = iterationCount * breathMs;

    const { isPulsing } = usePulsingBorder(
      effectivePulseDurationMs,
      pauseDuration * 1000,
      active,
    );

    const classes = useStyles(
      borderColor,
      borderWidth,
      borderRadius,
      speed,
      iterationCount,
      isPulsing,
      active,
      fadeDuration,
    );

    return (
      <Box ref={ref} sx={{ ...classes.root, ...sx } as SxProps} {...rest}>
        {children}
      </Box>
    );
  },
);

PulsingBorder.displayName = 'PulsingBorder';

export default PulsingBorder;
